import React, { useState, useEffect } from 'react';
import {
  Box, Typography, Paper, Button, TextField, MenuItem, Chip, Table, TableBody,
  TableCell, TableHead, TableRow, FormControlLabel, Checkbox, Divider, Alert,
  Radio, RadioGroup, FormControl, FormLabel
} from '@mui/material';
import {
  submitRepairDiagnosis,
  approveRepairDiagnosis,
  rejectRepairDiagnosis,
  saveRepairOtherPayments,
  markPartsReceived,
  completeRepairCollection,
  acceptQuotation,
  rejectQuotation,
  updateOutsourceWorkflow,
  setOutsourceRepairPrice,
  markOutsourceReceivedQa,
  informOutsourcePartialFix,
  informOutsourceTechOngoing
} from '../../services/api';
import {
  getCurrentUserName,
  getUserRole,
  canManageRepairs,
  isTechnician,
  isTechnicalCoordinator,
  isAdminOfficer,
  isFullAdmin
} from '../../utils/auth';
import { getErrorMessage } from '../../utils/errorMessage';
import PartPhotoImage from '../../components/PartPhotoImage';
import RepairCommonFaultField from './RepairCommonFaultField';
import RequestQuoteIcon from '@mui/icons-material/RequestQuote';
import EditIcon from '@mui/icons-material/Edit';
import VisibilityIcon from '@mui/icons-material/Visibility';
const emptyPart = () => ({
  part_name: '',
  supplier1_name: '',
  supplier1_cost: '',
  supplier2_name: '',
  supplier2_cost: '',
  supplier3_name: '',
  supplier3_cost: '',
  part_photo: '',
  part_photo_name: '',
  part_photo_url: ''
});
const hidePartSuppliers = () => isTechnician();
const emptyPayment = () => ({ reason: '', price: '' });
const parseOtherPayments = (raw) => {
  if (!raw) return [];
  let arr = raw;
  if (typeof raw === 'string') {
    try { arr = JSON.parse(raw); } catch (_) { return []; }
  }
  if (!Array.isArray(arr)) return [];
  return arr.map((p) => ({
    reason: p.reason || p.description || '',
    price: p.price != null ? String(p.price) : (p.unit_price != null ? String(p.unit_price) : '')
  })).filter((p) => p.reason || p.price);
};
const canAddOtherPayments = () => isFullAdmin() || isAdminOfficer();
const canManage = () => canManageRepairs();
const canStaffSeeParts = () =>
  ['admin', 'super_admin', 'manager', 'branch_manager', 'staff', 'branch_staff', 'technician', 'technical_coordinator', 'admin_officer'].includes(getUserRole());
const canPickOutsourceTech = () =>
  isTechnician() || isFullAdmin() || canManageRepairs() || isTechnicalCoordinator() || isAdminOfficer();
const isOutsourceLike = (t) => t === 'outsource' || t === 'both';
const isWarrantyFreeJob = (d) =>
  d?.service_coverage === 'warranty' && !d?.warranty_extras_billable;
const SEND_STATUSES = [
  { value: 'waiting_to_send', label: 'Waiting to send' },
  { value: 'still_in_office', label: 'Still in office' },
  { value: 'sent_to_technician', label: 'Sent to technician' }
];
/**
 * Expandable panels for workflow steps 2–4
 */
const WorkshopWorkflowPanels = ({
  data,
  expandedStep,
  setExpandedStep,
  outsourceTechList,
  onReload,
  setError,
  setSuccess,
  showQuoteInvoice,
  embedded = false,
  onCreateQuotation,
  onEditQuotation,
  onViewQuotation,
  hasQuotation = false
}) => {
  const today = new Date().toISOString().slice(0, 10);
  const userName = getCurrentUserName();
  const [itemSpec, setItemSpec] = useState(data?.diagnosis_item_specification || '');
  const [faultDesc, setFaultDesc] = useState(data?.diagnosis || '');
  const [laborCharge, setLaborCharge] = useState(
    data?.diagnosis_labor_charge != null ? String(data.diagnosis_labor_charge) : ''
  );
  const [repairType, setRepairType] = useState(data?.repair_location_type || 'in_house');
  const [parts, setParts] = useState(
    (data?.diagnosis_parts || []).length
      ? data.diagnosis_parts.map((p) => ({
          part_name: p.part_name || '',
          supplier1_name: p.supplier1_name || '',
          supplier1_cost: p.supplier1_cost ?? '',
          supplier2_name: p.supplier2_name || '',
          supplier2_cost: p.supplier2_cost ?? '',
          supplier3_name: p.supplier3_name || '',
          supplier3_cost: p.supplier3_cost ?? '',
          part_photo: '',
          part_photo_name: '',
          part_photo_url: p.part_photo_url || ''
        }))
      : [emptyPart()]
  );
  const [otherPayments, setOtherPayments] = useState(() => {
    const list = parseOtherPayments(data?.other_payments);
    return list.length ? list : [emptyPayment()];
  });
  const [savingPayments, setSavingPayments] = useState(false);
  const [savingDiag, setSavingDiag] = useState(false);
  const [outsourceTech, setOutsourceTech] = useState(data?.outsource_technician_name || '');
  const [approving, setApproving] = useState(false);
  const [approveParts, setApproveParts] = useState([]);
  const [approvalChannel, setApprovalChannel] = useState(data?.customer_approval_channel || 'phone');
  const [approvalRemark, setApprovalRemark] = useState(data?.customer_approval_remark || '');
  const [acceptingQuote, setAcceptingQuote] = useState(false);
  const [rejectingQuote, setRejectingQuote] = useState(false);
  const [rejectAction, setRejectAction] = useState('resend');
  const [rejectNotes, setRejectNotes] = useState('');
  const [completionSummary, setCompletionSummary] = useState(data?.repair_completion_summary || '');
  const [readyCollect, setReadyCollect] = useState(false);
  const [completing, setCompleting] = useState(false);
  const [markingParts, setMarkingParts] = useState(false);
  const [workflowStatus, setWorkflowStatus] = useState(data?.outsource_workflow_status || 'waiting_to_send');
  const [outsourcePrice, setOutsourcePrice] = useState(
    data?.outsource_repair_charges != null ? String(data.outsource_repair_charges) : ''
  );
  const [partialNote, setPartialNote] = useState(data?.outsource_partial_fix_note || '');
  const [osBusy, setOsBusy] = useState(false);
  useEffect(() => {
    setItemSpec(data?.diagnosis_item_specification || '');
    setFaultDesc(data?.diagnosis || '');
    setLaborCharge(
      data?.diagnosis_labor_charge != null && data?.diagnosis_labor_charge !== ''
        ? String(data.diagnosis_labor_charge)
        : ''
    );
    setRepairType(data?.repair_location_type || 'in_house');
    setOutsourceTech(data?.outsource_technician_name || '');
    setApprovalChannel(data?.customer_approval_channel || 'phone');
    setApprovalRemark(data?.customer_approval_remark || '');
    setCompletionSummary(data?.repair_completion_summary || '');
    setWorkflowStatus(data?.outsource_workflow_status || 'waiting_to_send');
    setOutsourcePrice(
      data?.outsource_repair_charges != null ? String(data.outsource_repair_charges) : ''
    );
    setPartialNote(data?.outsource_partial_fix_note || '');
    if ((data?.diagnosis_parts || []).length) {
      const mapped = data.diagnosis_parts.map((p) => ({
        id: p.id,
        part_name: p.part_name || '',
        supplier1_name: p.supplier1_name || '',
        supplier1_cost: p.supplier1_cost ?? '',
        supplier2_name: p.supplier2_name || '',
        supplier2_cost: p.supplier2_cost ?? '',
        supplier3_name: p.supplier3_name || '',
        supplier3_cost: p.supplier3_cost ?? '',
        selected_supplier: p.selected_supplier || '',
        part_photo_url: p.part_photo_url || ''
      }));
      setParts(
        mapped.map((p) => ({
          part_name: p.part_name,
          supplier1_name: p.supplier1_name,
          supplier1_cost: p.supplier1_cost,
          supplier2_name: p.supplier2_name,
          supplier2_cost: p.supplier2_cost,
          supplier3_name: p.supplier3_name,
          supplier3_cost: p.supplier3_cost,
          part_photo: '',
          part_photo_name: '',
          part_photo_url: p.part_photo_url || ''
        }))
      );
      setApproveParts(mapped);
    }
    const payments = parseOtherPayments(data?.other_payments);
    setOtherPayments(payments.length ? payments : [emptyPayment()]);
  }, [data?.id, data?.diagnosis, data?.diagnosis_approval_status, data?.parts_received, data?.status, data?.diagnosis_item_specification, data?.diagnosis_labor_charge, data?.outsource_workflow_status, data?.other_payments]);
  const toggle = (n) => setExpandedStep(expandedStep === n ? null : n);
  const updatePart = (idx, field, value) => {
    setParts((prev) => {
      const next = [...prev];
      next[idx] = { ...next[idx], [field]: value };
      return next;
    });
  };

  const handlePartPhoto = (idx, file) => {
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      setError('Part photo must be an image');
      return;
    }
    if (file.size > 8 * 1024 * 1024) {
      setError('Part photo must be under 8MB');
      return;
    }
    const reader = new FileReader();
    reader.onload = () => {
      setParts((prev) => {
        const next = [...prev];
        next[idx] = {
          ...next[idx],
          part_photo: reader.result,
          part_photo_name: file.name,
          part_photo_url: ''
        };
        return next;
      });
    };
    reader.readAsDataURL(file);
  };
  const updateApprovePart = (idx, field, value) => {
    setApproveParts((prev) => {
      const next = [...prev];
      next[idx] = { ...next[idx], [field]: value };
      return next;
    });
  };
  const handleSubmitDiagnosis = async () => {
    if (!faultDesc.trim()) {
      setError('Enter fault description');
      return;
    }
    if (repairType === 'in_house' || repairType === 'both') {
      const valid = parts.filter((p) => p.part_name.trim());
      if (!valid.length) {
        setError('Add at least one part for in-house / both repair');
        return;
      }
    }
    try {
      setSavingDiag(true);
      await submitRepairDiagnosis(data.id, {
        diagnosis: faultDesc.trim(),
        diagnosis_item_specification: itemSpec.trim() || null,
        diagnosis_labor_charge: laborCharge === '' ? null : parseFloat(laborCharge) || 0,
        repair_location_type: repairType,
        first_inspection_date: today,
        first_inspection_technician_name: userName,
        parts:
          repairType === 'in_house' || repairType === 'both'
            ? parts
                .filter((p) => p.part_name.trim())
                .map((p) => ({
                  part_name: p.part_name,
                  supplier1_name: hidePartSuppliers() ? '' : p.supplier1_name,
                  supplier1_cost: hidePartSuppliers() ? '' : p.supplier1_cost,
                  supplier2_name: hidePartSuppliers() ? '' : p.supplier2_name,
                  supplier2_cost: hidePartSuppliers() ? '' : p.supplier2_cost,
                  supplier3_name: hidePartSuppliers() ? '' : p.supplier3_name,
                  supplier3_cost: hidePartSuppliers() ? '' : p.supplier3_cost,
                  part_photo: p.part_photo || undefined,
                  part_photo_url: p.part_photo_url || undefined
                }))
            : []
      });
      setSuccess('Diagnosis saved and sent for approval — Step 2 completed');
      onReload();
    } catch (err) {
      setError(getErrorMessage(err, 'Failed to submit diagnosis'));
    } finally {
      setSavingDiag(false);
    }
  };
  const handleApproveDiagnosis = async () => {
    if (isOutsourceLike(data.repair_location_type) && !outsourceTech.trim()) {
      setError('Select outsource technician');
      return;
    }
    try {
      setApproving(true);
      const payload = {
        outsource_technician_name: isOutsourceLike(data.repair_location_type)
          ? outsourceTech.trim()
          : undefined,
        other_payments: otherPayments
          .filter((p) => (p.reason || '').trim())
          .map((p) => ({ reason: p.reason.trim(), price: parseFloat(p.price) || 0 }))
      };
      if (data.repair_location_type === 'in_house' || data.repair_location_type === 'both') {
        payload.part_selections = approveParts.map((p) => ({
          id: p.id,
          selected_supplier: parseInt(p.selected_supplier, 10) || null,
          selected_supplier_index: parseInt(p.selected_supplier, 10) || null,
          supplier1_name: p.supplier1_name,
          supplier1_cost: p.supplier1_cost,
          supplier2_name: p.supplier2_name,
          supplier2_cost: p.supplier2_cost,
          supplier3_name: p.supplier3_name,
          supplier3_cost: p.supplier3_cost
        }));
      }
      await approveRepairDiagnosis(data.id, payload);
      setSuccess('Diagnosis approved');
      onReload();
    } catch (err) {
      setError(getErrorMessage(err, 'Failed to approve'));
    } finally {
      setApproving(false);
    }
  };
  const handleRejectDiagnosis = async () => {
    try {
      setApproving(true);
      await rejectRepairDiagnosis(data.id, { notes: 'Please revise diagnosis' });
      setSuccess('Diagnosis rejected — technician can revise');
      onReload();
    } catch (err) {
      setError(getErrorMessage(err, 'Failed to reject'));
    } finally {
      setApproving(false);
    }
  };
  const handleSaveOtherPayments = async () => {
    const cleaned = otherPayments
      .filter((p) => (p.reason || '').trim())
      .map((p) => ({ reason: p.reason.trim(), price: parseFloat(p.price) || 0 }));
    try {
      setSavingPayments(true);
      await saveRepairOtherPayments(data.id, { other_payments: cleaned });
      setSuccess('Other payments saved');
      onReload();
    } catch (err) {
      setError(getErrorMessage(err, 'Failed to save other payments'));
    } finally {
      setSavingPayments(false);
    }
  };
  const handleAcceptQuotation = async () => {
    if (!approvalChannel) {
      setError('Select customer approval channel');
      return;
    }
    try {
      setAcceptingQuote(true);
      await acceptQuotation(data.id, {
        customer_approval_channel: approvalChannel,
        customer_approval_remark: approvalRemark,
        deposit_amount: 0
      });
      setSuccess('Quotation approved — Step 3 completed');
      onReload();
    } catch (err) {
      setError(getErrorMessage(err, 'Failed to approve quotation'));
    } finally {
      setAcceptingQuote(false);
    }
  };
  const handleRejectQuotation = async () => {
    try {
      setRejectingQuote(true);
      await rejectQuotation(data.id, {
        action: rejectAction,
        notes: rejectNotes || undefined
      });
      setSuccess(
        rejectAction === 'close_job'
          ? 'Quotation rejected — job closed'
          : 'Quotation rejected — draft kept for edit/resend'
      );
      onReload();
    } catch (err) {
      setError(getErrorMessage(err, 'Failed to reject quotation'));
    } finally {
      setRejectingQuote(false);
    }
  };
  const handlePartsReceived = async (received) => {
    try {
      setMarkingParts(true);
      await markPartsReceived(data.id, { parts_received: received });
      setSuccess(received ? 'Parts marked as received' : 'Parts marked as not received');
      onReload();
    } catch (err) {
      setError(getErrorMessage(err, 'Failed to update parts status'));
    } finally {
      setMarkingParts(false);
    }
  };
  const handleCompleteRepair = async () => {
    if (!completionSummary.trim()) {
      setError('Enter final repair summary');
      return;
    }
    if (!isOutsourceLike(data.repair_location_type) && !readyCollect) {
      setError('Tick "Ready for collection"');
      return;
    }
    try {
      setCompleting(true);
      await completeRepairCollection(data.id, {
        repair_completion_summary: completionSummary.trim(),
        ready_for_collection: true
      });
      setSuccess(
        isOutsourceLike(data.repair_location_type)
          ? 'Marked need to collect from outsource — receive in office then QA'
          : 'Repair ready for collection — see Ready to Collect page'
      );
      onReload();
    } catch (err) {
      setError(getErrorMessage(err, 'Failed to complete repair'));
    } finally {
      setCompleting(false);
    }
  };
  const handleWorkflowStatus = async (status) => {
    try {
      setOsBusy(true);
      await updateOutsourceWorkflow(data.id, {
        outsource_workflow_status: status,
        outsource_technician_name: outsourceTech.trim() || data.outsource_technician_name
      });
      setWorkflowStatus(status);
      setSuccess(`Outsource status: ${status.replace(/_/g, ' ')}`);
      onReload();
    } catch (err) {
      setError(getErrorMessage(err, 'Failed to update outsource status'));
    } finally {
      setOsBusy(false);
    }
  };
  const handleSaveOutsourcePrice = async () => {
    try {
      setOsBusy(true);
      await setOutsourceRepairPrice(data.id, {
        outsource_repair_charges: parseFloat(outsourcePrice) || 0
      });
      setSuccess('Outsource repair price saved — auto-fills quotation');
      onReload();
    } catch (err) {
      setError(getErrorMessage(err, 'Failed to save price'));
    } finally {
      setOsBusy(false);
    }
  };
  const handleReceivedQa = async () => {
    try {
      setOsBusy(true);
      await markOutsourceReceivedQa(data.id, {});
      setSuccess('Received in office — sent to QA');
      onReload();
    } catch (err) {
      setError(getErrorMessage(err, 'Failed to mark received / send QA'));
    } finally {
      setOsBusy(false);
    }
  };
  const handlePartialFix = async () => {
    if (!partialNote.trim()) {
      setError('Enter partial-fix note for outsource technician');
      return;
    }
    try {
      setOsBusy(true);
      await informOutsourcePartialFix(data.id, { partial_fix_note: partialNote.trim() });
      setSuccess('Outsource technician informed of partial fix');
      onReload();
    } catch (err) {
      setError(getErrorMessage(err, 'Failed to send partial-fix note'));
    } finally {
      setOsBusy(false);
    }
  };
  const handleInformOngoing = async () => {
    try {
      setOsBusy(true);
      await informOutsourceTechOngoing(data.id, {
        note: 'Customer approved — proceed with repair'
      });
      setSuccess('Outsource technician informed — repair ongoing');
      onReload();
    } catch (err) {
      setError(getErrorMessage(err, 'Failed to inform technician'));
    } finally {
      setOsBusy(false);
    }
  };
  const diagStatus = data.diagnosis_approval_status || 'draft';
  const warrantyFree = isWarrantyFreeJob(data);
  const diagnosisParts = data.diagnosis_parts || [];
  const partsGateOk =
    isOutsourceLike(data.repair_location_type) ||
    data.parts_received ||
    (warrantyFree && diagnosisParts.length === 0);
  const canShowComplete =
    partsGateOk &&
    (['approved', 'in_progress'].includes(data.status) ||
      (warrantyFree && diagStatus === 'approved')) &&
    !data.ready_for_collection &&
    data.outsource_workflow_status !== 'need_to_collect' &&
    data.outsource_workflow_status !== 'sent_to_qa';
  const lockedDiag = diagStatus === 'pending_approval' || diagStatus === 'approved';
  const osLike = isOutsourceLike(data.repair_location_type || repairType);
  return (
    <Box>
      {/* STEP 2 */}
      {(!embedded || expandedStep === 2) && (
      <Box sx={{ mb: embedded ? 0 : 1.5 }}>
        {!embedded && (
        <Button
          fullWidth
          variant={expandedStep === 2 ? 'contained' : 'outlined'}
          onClick={() => toggle(2)}
          sx={{ justifyContent: 'flex-start', textTransform: 'none' }}
        >
          Step 2: Initial Diagnosis {diagStatus !== 'draft' ? `(${diagStatus.replace(/_/g, ' ')})` : ''}
        </Button>
        )}
        {expandedStep === 2 && (
          <Paper variant="outlined" sx={{ p: embedded ? 0 : 2, mt: embedded ? 0 : 1, border: embedded ? 'none' : undefined }}>
            <Typography variant="subtitle1" fontWeight={700} gutterBottom>Repair Details</Typography>
            <TextField
              fullWidth
              size="small"
              label="First inspection date"
              value={data.first_inspection_date || today}
              InputProps={{ readOnly: true }}
              sx={{ mb: 1.5 }}
            />
            <TextField
              fullWidth
              size="small"
              label="First inspection technician"
              value={data.first_inspection_technician_name || userName}
              InputProps={{ readOnly: true }}
              sx={{ mb: 1.5 }}
            />
            <TextField
              fullWidth
              multiline
              minRows={2}
              label="Item specification"
              value={itemSpec}
              onChange={(e) => setItemSpec(e.target.value)}
              disabled={lockedDiag}
              sx={{ mb: 1.5 }}
              helperText="Describe the item / configuration above the fault"
            />
            <RepairCommonFaultField
              value={faultDesc}
              onChange={setFaultDesc}
              disabled={lockedDiag}
              setError={setError}
              setSuccess={setSuccess}
            />
            <TextField
              fullWidth
              size="small"
              type="number"
              label="Labor charge (optional)"
              value={laborCharge}
              onChange={(e) => setLaborCharge(e.target.value)}
              disabled={lockedDiag}
              sx={{ mb: 2 }}
              inputProps={{ min: 0, step: '0.01' }}
              helperText="Technician can set; used to auto-fill quotation labor"
            />
            <Typography variant="body2" sx={{ mb: 1 }}>Repair type</Typography>
            <Box sx={{ display: 'flex', gap: 1, mb: 2, flexWrap: 'wrap' }}>
              <Button
                variant={repairType === 'in_house' ? 'contained' : 'outlined'}
                disabled={lockedDiag}
                onClick={() => setRepairType('in_house')}
              >
                In-House Repair
              </Button>
              <Button
                variant={repairType === 'outsource' ? 'contained' : 'outlined'}
                color="secondary"
                disabled={lockedDiag}
                onClick={() => setRepairType('outsource')}
              >
                Outsource Repair
              </Button>
              <Button
                variant={repairType === 'both' ? 'contained' : 'outlined'}
                color="warning"
                disabled={lockedDiag}
                onClick={() => setRepairType('both')}
              >
                Both
              </Button>
            </Box>
            {(repairType === 'in_house' || repairType === 'both') && diagStatus !== 'approved' && (
              <Box sx={{ mb: 2 }}>
                <Typography variant="subtitle2" gutterBottom>Parts needed to repair</Typography>
                {parts.map((p, idx) => (
                  <Paper key={idx} variant="outlined" sx={{ p: 1.5, mb: 1.5 }}>
                    <TextField
                      fullWidth
                      size="small"
                      label="Part name"
                      value={p.part_name}
                      onChange={(e) => updatePart(idx, 'part_name', e.target.value)}
                      disabled={diagStatus === 'pending_approval'}
                      sx={{ mb: 1 }}
                    />
                    {!hidePartSuppliers() && (
                      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 1, mb: 1 }}>
                        {[1, 2, 3].map((n) => (
                          <React.Fragment key={n}>
                            <TextField
                              size="small"
                              label={`Supplier ${n}`}
                              value={p[`supplier${n}_name`] || ''}
                              onChange={(e) => updatePart(idx, `supplier${n}_name`, e.target.value)}
                              disabled={diagStatus === 'pending_approval'}
                            />
                            <TextField
                              size="small"
                              type="number"
                              label={`Cost ${n}`}
                              value={p[`supplier${n}_cost`] ?? ''}
                              onChange={(e) => updatePart(idx, `supplier${n}_cost`, e.target.value)}
                              disabled={diagStatus === 'pending_approval'}
                              inputProps={{ min: 0, step: '0.01' }}
                            />
                          </React.Fragment>
                        ))}
                      </Box>
                    )}
                    <Box>
                      <Button
                        size="small"
                        variant="outlined"
                        component="label"
                        disabled={diagStatus === 'pending_approval'}
                      >
                        Upload part photo
                        <input
                          type="file"
                          hidden
                          accept="image/*"
                          onChange={(e) => {
                            const f = e.target.files?.[0];
                            e.target.value = '';
                            handlePartPhoto(idx, f);
                          }}
                        />
                      </Button>
                      {(p.part_photo_name || p.part_photo || p.part_photo_url) && (
                        <Typography variant="caption" sx={{ ml: 1 }} color="text.secondary">
                          {p.part_photo_name || 'Photo attached'}
                        </Typography>
                      )}
                      {(p.part_photo || p.part_photo_url) && (
                        <PartPhotoImage
                          photoUrl={p.part_photo_url}
                          dataUrl={p.part_photo || undefined}
                          thumb={false}
                          sx={{ mt: 1, maxHeight: 120, maxWidth: '100%' }}
                        />
                      )}
                    </Box>
                  </Paper>
                ))}
                {diagStatus !== 'pending_approval' && (
                  <Button size="small" onClick={() => setParts((prev) => [...prev, emptyPart()])}>
                    + Add part
                  </Button>
                )}
              </Box>
            )}
            {canAddOtherPayments() && (
              <Box sx={{ mb: 2 }}>
                <Typography variant="subtitle2" gutterBottom>Any other payment</Typography>
                {otherPayments.map((p, idx) => (
                  <Box key={idx} sx={{ display: 'flex', gap: 1, mb: 1, flexWrap: 'wrap' }}>
                    <TextField
                      size="small"
                      label="Reason"
                      value={p.reason}
                      onChange={(e) => {
                        const val = e.target.value;
                        setOtherPayments((prev) => {
                          const next = [...prev];
                          next[idx] = { ...next[idx], reason: val };
                          return next;
                        });
                      }}
                      sx={{ flex: 1, minWidth: 180 }}
                    />
                    <TextField
                      size="small"
                      type="number"
                      label="Price"
                      value={p.price}
                      onChange={(e) => {
                        const val = e.target.value;
                        setOtherPayments((prev) => {
                          const next = [...prev];
                          next[idx] = { ...next[idx], price: val };
                          return next;
                        });
                      }}
                      inputProps={{ min: 0, step: '0.01' }}
                      sx={{ width: 140 }}
                    />
                  </Box>
                ))}
                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                  <Button size="small" onClick={() => setOtherPayments((prev) => [...prev, emptyPayment()])}>
                    + Add payment
                  </Button>
                  <Button size="small" variant="outlined" disabled={savingPayments} onClick={handleSaveOtherPayments}>
                    {savingPayments ? 'Saving...' : 'Save other payments'}
                  </Button>
                </Box>
              </Box>
            )}
            {diagStatus !== 'approved' && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap', mb: 1 }}>
                <Button
                  variant="contained"
                  color={diagStatus === 'pending_approval' ? 'success' : 'primary'}
                  disabled={savingDiag || diagStatus === 'pending_approval'}
                  onClick={handleSubmitDiagnosis}
                  sx={diagStatus === 'pending_approval' ? { bgcolor: 'success.main', '&:disabled': { bgcolor: 'success.main', color: '#fff' } } : undefined}
                >
                  {savingDiag ? 'Saving...' : 'Save & Send Approve'}
                </Button>
                {diagStatus === 'pending_approval' && (
                  <Typography color="success.main" fontWeight={700}>
                    Send for approve
                  </Typography>
                )}
              </Box>
            )}
            {/* Admin/Manager approval for pending diagnosis */}
            {canManage() && diagStatus === 'pending_approval' && (
              <Box sx={{ mt: 2, p: 1.5, bgcolor: 'warning.50', borderRadius: 1 }}>
                <Alert severity="info" sx={{ mb: 1 }}>
                  Pending approval —{' '}
                  {data.repair_location_type === 'outsource'
                    ? 'Outsource'
                    : data.repair_location_type === 'both'
                      ? 'Both'
                      : 'In-house'}
                </Alert>
                {isOutsourceLike(data.repair_location_type) && (
                  <TextField
                    select
                    fullWidth
                    size="small"
                    label="Select outsource technician *"
                    value={outsourceTech}
                    onChange={(e) => setOutsourceTech(e.target.value)}
                    sx={{ mb: 1 }}
                    disabled={!canPickOutsourceTech() && !canManage()}
                  >
                    <MenuItem value=""><em>Select</em></MenuItem>
                    {outsourceTechList.map((t) => (
                      <MenuItem key={t.id} value={t.name}>{t.name}</MenuItem>
                    ))}
                  </TextField>
                )}
                {(data.repair_location_type === 'in_house' || data.repair_location_type === 'both') && approveParts.length > 0 && (
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="subtitle2" gutterBottom>
                      {hidePartSuppliers() ? 'Parts submitted' : 'Select supplier (edit names/costs if needed)'}
                    </Typography>
                    {approveParts.map((p, idx) => (
                      <Paper key={p.id || idx} variant="outlined" sx={{ p: 1.5, mb: 1.5 }}>
                        <Typography fontWeight={600} sx={{ mb: 1 }}>{p.part_name}</Typography>
                        {p.part_photo_url && (
                          <PartPhotoImage
                            photoUrl={p.part_photo_url}
                            thumb={false}
                            sx={{ mb: 1, maxHeight: 100, maxWidth: '100%' }}
                          />
                        )}
                        {!hidePartSuppliers() && (
                          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr auto' }, gap: 1, alignItems: 'center' }}>
                            {[1, 2, 3].map((n) => (
                              <React.Fragment key={n}>
                                <TextField
                                  size="small"
                                  label={`Supplier ${n}`}
                                  value={p[`supplier${n}_name`] || ''}
                                  onChange={(e) => updateApprovePart(idx, `supplier${n}_name`, e.target.value)}
                                />
                                <TextField
                                  size="small"
                                  type="number"
                                  label={`Cost ${n}`}
                                  value={p[`supplier${n}_cost`] ?? ''}
                                  onChange={(e) => updateApprovePart(idx, `supplier${n}_cost`, e.target.value)}
                                  inputProps={{ min: 0, step: '0.01' }}
                                />
                                <FormControlLabel
                                  control={
                                    <Radio
                                      checked={String(p.selected_supplier) === String(n)}
                                      onChange={() => updateApprovePart(idx, 'selected_supplier', n)}
                                    />
                                  }
                                  label={`Use ${n}`}
                                />
                              </React.Fragment>
                            ))}
                          </Box>
                        )}
                      </Paper>
                    ))}
                  </Box>
                )}
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <Button variant="contained" color="success" disabled={approving} onClick={handleApproveDiagnosis}>
                    Approve
                  </Button>
                  <Button variant="outlined" color="error" disabled={approving} onClick={handleRejectDiagnosis}>
                    Reject
                  </Button>
                </Box>
              </Box>
            )}
            {diagStatus === 'approved' && (
              <Box sx={{ mt: 1 }}>
                <Chip label="Diagnosis approved" color="success" sx={{ mb: 1 }} />
                {data.diagnosis_item_specification && (
                  <Typography variant="body2" sx={{ mb: 0.5 }}>
                    <strong>Item spec:</strong> {data.diagnosis_item_specification}
                  </Typography>
                )}
                {data.diagnosis_labor_charge != null && data.diagnosis_labor_charge !== '' && (
                  <Typography variant="body2">
                    <strong>Labor charge:</strong> {data.diagnosis_labor_charge}
                  </Typography>
                )}
                {diagnosisParts.length > 0 && (
                  <Table size="small" sx={{ mt: 1 }}>
                    <TableHead>
                      <TableRow>
                        <TableCell>Part</TableCell>
                        <TableCell>Photo</TableCell>
                        {!hidePartSuppliers() && <TableCell>Selected supplier</TableCell>}
                        <TableCell>Status</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {diagnosisParts.map((p) => {
                        const n = p.selected_supplier;
                        const name = n ? p[`supplier${n}_name`] : null;
                        const cost = n ? p[`supplier${n}_cost`] : null;
                        return (
                          <TableRow key={p.id}>
                            <TableCell>{p.part_name}</TableCell>
                            <TableCell>
                              {p.part_photo_url ? (
                                <PartPhotoImage photoUrl={p.part_photo_url} />
                              ) : '—'}
                            </TableCell>
                            {!hidePartSuppliers() && (
                              <TableCell>
                                {name ? `${name} / ${cost}` : '-'}
                              </TableCell>
                            )}
                            <TableCell>{p.status}</TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                )}
                {parseOtherPayments(data.other_payments).length > 0 && (
                  <Box sx={{ mt: 1 }}>
                    <Typography variant="body2" fontWeight={600}>Other payments</Typography>
                    {parseOtherPayments(data.other_payments).map((p, i) => (
                      <Typography key={i} variant="body2">
                        {p.reason}: {p.price}
                      </Typography>
                    ))}
                  </Box>
                )}
              </Box>
            )}
          </Paper>
        )}
      </Box>
      )}
      {/* STEP 3 */}
      {(!embedded || expandedStep === 3) && (
      <Box sx={{ mb: embedded ? 0 : 1.5 }}>
        {!embedded && (
        <Button
          fullWidth
          variant={expandedStep === 3 ? 'contained' : 'outlined'}
          onClick={() => toggle(3)}
          sx={{ justifyContent: 'flex-start', textTransform: 'none' }}
        >
          Step 3: Provide Estimate / Quotation
        </Button>
        )}
        {expandedStep === 3 && (
          <Paper variant="outlined" sx={{ p: embedded ? 0 : 2, mt: embedded ? 0 : 1, border: embedded ? 'none' : undefined }}>
            {!showQuoteInvoice ? (
              <Typography color="text.secondary">Admin / Manager create and approve quotations.</Typography>
            ) : (
              <>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                  Create a quotation from the diagnosis, send it to the customer, then record how they approved or rejected.
                </Typography>
                {showQuoteInvoice && (
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
                    {(!warrantyFree) && (
                      <Button
                        variant="contained"
                        color="secondary"
                        startIcon={<RequestQuoteIcon />}
                        onClick={onCreateQuotation}
                      >
                        Create quotation
                      </Button>
                    )}
                    {hasQuotation && (
                      <>
                        <Button variant="outlined" color="secondary" startIcon={<VisibilityIcon />} onClick={onViewQuotation}>
                          View quotation
                        </Button>
                        <Button variant="outlined" color="secondary" startIcon={<EditIcon />} onClick={onEditQuotation}>
                          Edit quotation
                        </Button>
                      </>
                    )}
                  </Box>
                )}
                {data.diagnosis_labor_charge != null && data.diagnosis_labor_charge !== '' && (
                  <Alert severity="info" sx={{ mb: 1 }}>
                    Diagnosis labor charge: {data.diagnosis_labor_charge} (auto-fills Create Quotation)
                  </Alert>
                )}
                {isOutsourceLike(data.repair_location_type) && Number(data.outsource_repair_charges) > 0 && (
                  <Alert severity="info" sx={{ mb: 1 }}>
                    Outsource tech price: {data.outsource_repair_charges} (auto-fills Create Quotation — editable)
                  </Alert>
                )}
                {warrantyFree && (
                  <Alert severity="success" sx={{ mb: 1 }}>
                    <strong>Free warranty</strong> — quotation is optional. After diagnosis is approved, go to{' '}
                    <strong>Step 4</strong> to complete the repair. Turn on paid extras only if the customer wants billable items.
                  </Alert>
                )}
                {data.status === 'quoted' && (
                  <Box sx={{ mt: 1 }}>
                    <TextField
                      select
                      fullWidth
                      size="small"
                      label="Customer approved via"
                      value={approvalChannel}
                      onChange={(e) => setApprovalChannel(e.target.value)}
                      sx={{ mb: 1.5 }}
                    >
                      <MenuItem value="phone">Phone</MenuItem>
                      <MenuItem value="whatsapp">WhatsApp</MenuItem>
                      <MenuItem value="email">Email</MenuItem>
                      <MenuItem value="text">Text / SMS</MenuItem>
                    </TextField>
                    <TextField
                      fullWidth
                      size="small"
                      multiline
                      minRows={2}
                      label="Remark"
                      value={approvalRemark}
                      onChange={(e) => setApprovalRemark(e.target.value)}
                      sx={{ mb: 1.5 }}
                    />
                    <Button variant="contained" color="success" disabled={acceptingQuote || rejectingQuote} onClick={handleAcceptQuotation}>
                      {acceptingQuote ? 'Approving...' : 'Approve Quotation (auto-check Step 3)'}
                    </Button>
                    {canManage() && (
                      <Box sx={{ mt: 2, p: 1.5, border: '1px solid', borderColor: 'error.light', borderRadius: 1 }}>
                        <FormControl component="fieldset" sx={{ mb: 1 }}>
                          <FormLabel component="legend">Customer rejected quotation</FormLabel>
                          <RadioGroup
                            row
                            value={rejectAction}
                            onChange={(e) => setRejectAction(e.target.value)}
                          >
                            <FormControlLabel value="resend" control={<Radio />} label="Resend / partial fix (edit quotation)" />
                            <FormControlLabel
                              value="close_job"
                              control={<Radio />}
                              label={
                                isOutsourceLike(data.repair_location_type)
                                  ? 'Collect not repaired (inform outsource)'
                                  : 'Close job'
                              }
                            />
                          </RadioGroup>
                        </FormControl>
                        <TextField
                          fullWidth
                          size="small"
                          label={
                            isOutsourceLike(data.repair_location_type) && rejectAction === 'resend'
                              ? 'Partial fix note for outsource tech'
                              : 'Reject notes'
                          }
                          value={rejectNotes}
                          onChange={(e) => setRejectNotes(e.target.value)}
                          sx={{ mb: 1 }}
                        />
                        <Button
                          variant="outlined"
                          color="error"
                          disabled={rejectingQuote || acceptingQuote}
                          onClick={handleRejectQuotation}
                        >
                          {rejectingQuote
                            ? 'Saving...'
                            : rejectAction === 'close_job'
                              ? isOutsourceLike(data.repair_location_type)
                                ? 'Reject & Ready to Collect'
                                : 'Reject & Close Job'
                              : 'Reject & Keep Editable'}
                        </Button>
                      </Box>
                    )}
                    {isOutsourceLike(data.repair_location_type) && canManage() && (
                      <Box sx={{ mt: 2 }}>
                        <Button size="small" variant="outlined" disabled={osBusy} onClick={handleInformOngoing}>
                          Inform outsource tech — repair ongoing
                        </Button>
                      </Box>
                    )}
                  </Box>
                )}
                {['approved', 'in_progress'].includes(data.status) &&
                  isOutsourceLike(data.repair_location_type) &&
                  canManage() && (
                  <Box sx={{ mt: 2 }}>
                    <TextField
                      fullWidth
                      size="small"
                      multiline
                      minRows={2}
                      label="Partial fix note (customer said fix this part only)"
                      value={partialNote}
                      onChange={(e) => setPartialNote(e.target.value)}
                      sx={{ mb: 1 }}
                    />
                    <Button size="small" variant="outlined" disabled={osBusy} onClick={handlePartialFix}>
                      Inform outsource of partial fix
                    </Button>
                  </Box>
                )}
                {['approved', 'in_progress', 'completed', 'delivered'].includes(data.status) && data.customer_approval_channel && (
                  <Chip
                    color="success"
                    label={`Customer approved via ${data.customer_approval_channel}`}
                  />
                )}
                {data.customer_rejected_quotation && (
                  <Chip color="error" label="Customer rejected quotation / item not repaired" sx={{ ml: 1 }} />
                )}
                {data.status === 'cancelled' && (
                  <Chip color="error" label="Job closed after quotation reject" />
                )}
                {data.status === 'diagnosis' && diagStatus === 'approved' && (
                  <Alert severity="info">Create quotation using “Create Quotation” below.</Alert>
                )}
              </>
            )}
          </Paper>
        )}
      </Box>
      )}
      {/* STEP 4 */}
      {(!embedded || expandedStep === 4) && (
      <Box sx={{ mb: embedded ? 0 : 1.5 }}>
        {!embedded && (
        <Button
          fullWidth
          variant={expandedStep === 4 ? 'contained' : 'outlined'}
          onClick={() => toggle(4)}
          sx={{ justifyContent: 'flex-start', textTransform: 'none' }}
        >
          Step 4: Repair Process
        </Button>
        )}
        {expandedStep === 4 && (
          <Paper variant="outlined" sx={{ p: embedded ? 0 : 2, mt: embedded ? 0 : 1, border: embedded ? 'none' : undefined }}>
            {(data.repair_location_type === 'in_house' || data.repair_location_type === 'both') && canStaffSeeParts() && (
              <Box sx={{ mb: 2 }}>
                <Typography variant="subtitle2" gutterBottom>Parts received?</Typography>
                <Chip
                  label={data.parts_received ? `Received${data.parts_received_by ? ` by ${data.parts_received_by}` : ''}` : 'Not received'}
                  color={data.parts_received ? 'success' : 'warning'}
                  sx={{ mr: 1, mb: 1 }}
                />
                {(isTechnician() || canManage()) && !data.ready_for_collection && (
                  <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                    <Button
                      size="small"
                      variant="contained"
                      color="success"
                      disabled={markingParts || data.parts_received}
                      onClick={() => handlePartsReceived(true)}
                    >
                      Mark Parts Received
                    </Button>
                    <Button
                      size="small"
                      variant="outlined"
                      disabled={markingParts || !data.parts_received}
                      onClick={() => handlePartsReceived(false)}
                    >
                      Mark Not Received
                    </Button>
                  </Box>
                )}
                {diagnosisParts.length > 0 && (
                  <Table size="small" sx={{ mt: 1 }}>
                    <TableHead>
                      <TableRow>
                        <TableCell>Part</TableCell>
                        <TableCell>Photo</TableCell>
                        {!hidePartSuppliers() && <TableCell>Suppliers</TableCell>}
                        <TableCell>Status</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {diagnosisParts.map((p) => (
                        <TableRow key={p.id}>
                          <TableCell>{p.part_name}</TableCell>
                          <TableCell>
                            {p.part_photo_url ? (
                              <PartPhotoImage photoUrl={p.part_photo_url} />
                            ) : '—'}
                          </TableCell>
                          {!hidePartSuppliers() && (
                            <TableCell>
                              {[p.supplier1_name, p.supplier2_name, p.supplier3_name].filter(Boolean).join(' | ') || '-'}
                            </TableCell>
                          )}
                          <TableCell>{p.status}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}
              </Box>
            )}
            {osLike && (
              <Box sx={{ mb: 2 }}>
                <Typography variant="body2" sx={{ mb: 1 }}>
                  Outsource technician: <strong>{data.outsource_technician_name || 'Not assigned'}</strong>
                  {data.outsource_workflow_status && (
                    <Chip
                      size="small"
                      sx={{ ml: 1 }}
                      label={(data.outsource_workflow_status || '').replace(/_/g, ' ')}
                      color="secondary"
                    />
                  )}
                </Typography>
                {canPickOutsourceTech() && !data.ready_for_collection && (
                  <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 1.5 }}>
                    {SEND_STATUSES.map((s) => (
                      <Button
                        key={s.value}
                        size="small"
                        variant={workflowStatus === s.value || data.outsource_workflow_status === s.value ? 'contained' : 'outlined'}
                        color="secondary"
                        disabled={osBusy}
                        onClick={() => handleWorkflowStatus(s.value)}
                      >
                        {s.label}
                      </Button>
                    ))}
                  </Box>
                )}
                {['sent_to_technician', 'awaiting_price', 'price_received'].includes(
                  data.outsource_workflow_status || workflowStatus
                ) && (
                  <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start', flexWrap: 'wrap', mb: 1.5 }}>
                    <TextField
                      size="small"
                      type="number"
                      label="Outsource repair price"
                      value={outsourcePrice}
                      onChange={(e) => setOutsourcePrice(e.target.value)}
                      inputProps={{ min: 0, step: '0.01' }}
                      sx={{ minWidth: 180 }}
                    />
                    <Button size="small" variant="contained" disabled={osBusy} onClick={handleSaveOutsourcePrice}>
                      Save price
                    </Button>
                  </Box>
                )}
                {data.outsource_workflow_status === 'need_to_collect' && (
                  <Alert severity="warning" sx={{ mb: 1 }}>
                    Need to collect from outsource.
                    {(canManage() || canPickOutsourceTech()) && (
                      <Button size="small" sx={{ ml: 1 }} disabled={osBusy} onClick={handleReceivedQa}>
                        Received in office → Send to QA
                      </Button>
                    )}
                  </Alert>
                )}
                {data.outsource_workflow_status === 'sent_to_qa' && (
                  <Alert severity="info" sx={{ mb: 1 }}>
                    Sent to QA — open the <strong>QA</strong> page.
                  </Alert>
                )}
                {data.outsource_partial_fix_note && (
                  <Alert severity="info" sx={{ mb: 1 }}>
                    Partial fix note: {data.outsource_partial_fix_note}
                  </Alert>
                )}
              </Box>
            )}
            {canShowComplete && (
              <Box>
                <Divider sx={{ my: 1 }} />
                <Typography variant="subtitle1" fontWeight={700} gutterBottom>
                  Repair Completion Details
                </Typography>
                <TextField
                  fullWidth
                  multiline
                  minRows={3}
                  label="Final repair summary"
                  value={completionSummary}
                  onChange={(e) => setCompletionSummary(e.target.value)}
                  sx={{ mb: 1.5 }}
                />
                {!isOutsourceLike(data.repair_location_type) && (
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={readyCollect}
                        onChange={(e) => setReadyCollect(e.target.checked)}
                      />
                    }
                    label="Ready for collection"
                  />
                )}
                {isOutsourceLike(data.repair_location_type) && (
                  <Alert severity="info" sx={{ mb: 1 }}>
                    Completing marks <strong>need to collect</strong> from outsource (then receive → QA).
                  </Alert>
                )}
                <Box sx={{ mt: 1 }}>
                  <Button variant="contained" color="success" disabled={completing} onClick={handleCompleteRepair}>
                    {completing
                      ? 'Saving...'
                      : isOutsourceLike(data.repair_location_type)
                        ? 'Complete — Need to Collect'
                        : 'Save — Ready for Collection'}
                  </Button>
                </Box>
              </Box>
            )}
            {data.ready_for_collection && (
              <Alert severity="success">
                Ready for collection — open the <strong>Ready to Collect</strong> page.
                {data.customer_rejected_quotation ? ' (Customer rejected quotation / item not repaired)' : ''}
              </Alert>
            )}
          </Paper>
        )}
      </Box>
      )}
    </Box>
  );
};
export default WorkshopWorkflowPanels;
